import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Users, Calendar, Clock, ChevronLeft, ChevronRight,
  UserCheck, UserX, Coffee, AlertCircle, CheckCircle,
  LogIn, LogOut, RefreshCcw, Search, Globe, Timer, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';
import { todayUtc } from '@/utils/dateUtils';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtDuration = (seconds) => {
  if (!seconds || seconds < 0) return '-';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
};

const fmtMins = (minutes) => {
  if (!minutes || minutes <= 0) return '-';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const getActivityStatus = (att, currentTime) => {
  if (!att?.check_in) return { status: 'not_in', label: 'Not Checked In', color: 'bg-gray-100 text-gray-600' };
  const activeBreak = att.breaks?.find(b => b.start_time && !b.end_time);
  if (activeBreak) {
    const dur = Math.max(0, Math.floor((currentTime - new Date(activeBreak.start_time)) / 1000));
    const isForce = activeBreak.break_type === 'force' || activeBreak.is_force_break;
    return {
      status: 'on_break',
      label: isForce ? `Force Break (${fmtDuration(dur)})` : `On Break (${fmtDuration(dur)})`,
      color: isForce ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700',
      dur,
    };
  }
  if (att.check_out) {
    return att.force_checkout
      ? { status: 'force_out', label: 'Force Checked Out', color: 'bg-red-100 text-red-700' }
      : { status: 'checked_out', label: 'Checked Out', color: 'bg-blue-100 text-blue-700' };
  }
  const dur = Math.max(0, Math.floor((currentTime - new Date(att.check_in)) / 1000));
  return { status: 'ready', label: `Ready (${fmtDuration(dur)})`, color: 'bg-emerald-100 text-emerald-700', dur };
};

const getBreakSecs = (att, currentTime) => {
  if (!att?.breaks?.length) return 0;
  return att.breaks.reduce((sum, b) => {
    if (!b.start_time) return sum;
    const end = b.end_time ? new Date(b.end_time) : currentTime;
    return sum + Math.max(0, Math.floor((end - new Date(b.start_time)) / 1000));
  }, 0);
};

const getWorkSecs = (att, currentTime) => {
  if (!att?.check_in) return 0;
  const end = att.check_out ? new Date(att.check_out) : currentTime;
  return Math.max(0, Math.floor((end - new Date(att.check_in)) / 1000) - getBreakSecs(att, currentTime));
};

// ─── component ──────────────────────────────────────────────────────────────
const ManagerAttendancePage = () => {
  const { t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdate, setLastUpdate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const initialDateSet = useRef(false);
  const autoRefreshRef = useRef(null);

  const isToday = selectedDate === todayUtc();

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh every 10s when viewing today
  useEffect(() => {
    if (isToday && selectedTeam) {
      autoRefreshRef.current = setInterval(() => fetchAttendance(selectedTeam, true), 10000);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [isToday, selectedTeam, selectedDate]);

  useEffect(() => { fetchTeams(); }, []);

  useEffect(() => {
    if (selectedTeam) fetchAttendance(selectedTeam);
  }, [selectedTeam, selectedDate]);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/manager/teams`, { withCredentials: true });
      setTeams(res.data);
      if (res.data.length > 0) setSelectedTeam(res.data[0].team_id);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Access denied. Manager role required.');
      else toast.error('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = useCallback(async (teamId, silent = false) => {
    if (!silent) setAttLoading(true);
    try {
      const params = initialDateSet.current && selectedDate ? { date: selectedDate } : {};
      const res = await axios.get(`${API}/manager/teams/${teamId}/attendance`, {
        params,
        withCredentials: true,
      });
      const data = res.data;
      const tz = data.timezone || 'UTC';
      setTimezone(tz);
      if (!initialDateSet.current) {
        initialDateSet.current = true;
        if (data.date) setSelectedDate(data.date);
      }
      setAttendanceData(data);
      setLastUpdate(new Date());
    } catch {
      if (!silent) toast.error('Failed to fetch attendance');
    } finally {
      if (!silent) setAttLoading(false);
    }
  }, [selectedDate]);

  const changeDate = (days) => {
    if (!selectedDate) return;
    const [y, m, d] = selectedDate.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + days));
    setSelectedDate(next.toISOString().split('T')[0]);
  };

  // ── stats ────────────────────────────────────────────────────────────────
  const staff = attendanceData?.staff || [];
  const filtered = staff.filter(s =>
    !searchTerm || s.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = staff.reduce((acc, s) => {
    const att = s.attendance;
    if (!att) { acc.notMarked++; return acc; }
    if (att.check_in && !att.check_out) acc.checkedIn++;
    if (att.check_out) acc.checkedOut++;
    if (att.status === 'absent') acc.absent++;
    if (att.status === 'late') acc.late++;
    const onBreak = att.breaks?.some(b => b.start_time && !b.end_time);
    if (onBreak) {
      acc.onBreak++;
      if (att.breaks?.some(b => b.break_type === 'force' && !b.end_time)) acc.forceBreak++;
    } else if (att.check_in && !att.check_out) {
      acc.ready++;
    }
    return acc;
  }, { checkedIn: 0, checkedOut: 0, absent: 0, late: 0, notMarked: 0, onBreak: 0, ready: 0, forceBreak: 0 });

  const statCards = [
    { label: 'Checked In', value: stats.checkedIn, icon: LogIn, cls: 'text-green-600 bg-green-100 border-green-200' },
    { label: 'Checked Out', value: stats.checkedOut, icon: LogOut, cls: 'text-blue-600 bg-blue-100 border-blue-200' },
    { label: 'Absent', value: stats.absent, icon: UserX, cls: 'text-red-600 bg-red-100 border-red-200' },
    { label: 'Late', value: stats.late, icon: Clock, cls: 'text-amber-600 bg-amber-100 border-amber-200' },
    { label: 'Not Marked', value: stats.notMarked, icon: AlertCircle, cls: 'text-gray-600 bg-gray-100 border-gray-200' },
  ];
  const breakCards = [
    { label: 'On Break', value: stats.onBreak, icon: Coffee, cls: 'text-orange-600 bg-orange-100 border-orange-200' },
    { label: 'Ready', value: stats.ready, icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-100 border-emerald-200' },
    { label: 'Force Break', value: stats.forceBreak, icon: Zap, cls: 'text-purple-600 bg-purple-100 border-purple-200' },
  ];

  // ── sorted: on_break first, then ready, then rest ────────────────────────
  const sorted = [...filtered].sort((a, b) => {
    const order = { on_break: 0, ready: 1, checked_out: 2, force_out: 3, not_in: 4 };
    const aa = getActivityStatus(a.attendance, currentTime);
    const bb = getActivityStatus(b.attendance, currentTime);
    return (order[aa.status] ?? 5) - (order[bb.status] ?? 5);
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
    </div>
  );

  if (teams.length === 0) return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
        <Users className="w-12 h-12 text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900">No Teams Assigned</h3>
        <p className="text-gray-500 text-sm">You are not assigned to any teams yet.</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="manager-attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Team Attendance</h2>
          <p className="text-gray-500 mt-1 flex items-center gap-2 text-sm">
            Real-time attendance for your teams
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              <Globe className="w-3 h-3" />{timezone}
            </span>
          </p>
        </div>
        {/* Date navigation */}
        <div className="flex items-center gap-2">
          {isToday && lastUpdate && (
            <span className="text-xs text-gray-400 hidden sm:block">
              Updated {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="prev-date-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            data-testid="date-picker"
          />
          <Button variant="outline" size="icon" onClick={() => changeDate(1)} disabled={isToday} data-testid="next-date-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => selectedTeam && fetchAttendance(selectedTeam)} data-testid="refresh-btn">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Live indicator */}
      {isToday && (
        <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          Live — auto-refreshes every 10 seconds
        </div>
      )}

      {/* Team tabs */}
      <div className="flex gap-2 flex-wrap">
        {teams.map(team => (
          <button
            key={team.team_id}
            onClick={() => setSelectedTeam(team.team_id)}
            data-testid={`team-tab-${team.team_id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
              selectedTeam === team.team_id
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
            }`}
          >
            <Users className="w-4 h-4" />
            {team.name}
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
              selectedTeam === team.team_id ? 'bg-white/20' : 'bg-gray-100'
            }`}>{team.staff_count}</span>
          </button>
        ))}
      </div>

      {attLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </CardContent>
        </Card>
      ) : attendanceData ? (
        <>
          {/* Stat cards row 1 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {statCards.map(c => (
              <Card key={c.label} className={`border-2 ${c.cls.split(' ').find(x=>x.startsWith('border'))} shadow-sm`}
                data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${c.cls.split(' ').find(x=>x.startsWith('bg'))}`}>
                      <c.icon className={`w-5 h-5 ${c.cls.split(' ').find(x=>x.startsWith('text'))}`} />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900">{c.value}</p>
                      <p className="text-xs text-gray-500 font-medium">{c.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stat cards row 2 */}
          <div className="grid grid-cols-3 gap-4">
            {breakCards.map(c => (
              <Card key={c.label} className={`border-2 ${c.cls.split(' ').find(x=>x.startsWith('border'))} shadow-sm`}
                data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <CardContent className="py-5 flex flex-col items-center justify-center">
                  <p className="text-4xl font-bold text-gray-900">{c.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{c.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Attendance table */}
          <Card className="shadow-lg" data-testid="attendance-table">
            <CardHeader className="border-b bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                    {attendanceData.team?.name}
                  </CardTitle>
                  <CardDescription>
                    {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                    &nbsp;—&nbsp;{staff.length} staff member{staff.length !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
                <div className="text-sm text-gray-500 text-right">
                  {lastUpdate && <p>Last updated: {lastUpdate.toLocaleTimeString()}</p>}
                </div>
              </div>
              {/* Search */}
              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 max-w-xs"
                  data-testid="staff-search"
                />
                {searchTerm && (
                  <p className="text-xs text-gray-400 mt-1">
                    Showing {filtered.length} of {staff.length}
                  </p>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Check In</TableHead>
                    <TableHead className="font-semibold">Check Out</TableHead>
                    <TableHead className="font-semibold">Work Time</TableHead>
                    <TableHead className="font-semibold">Break</TableHead>
                    <TableHead className="font-semibold">Late By</TableHead>
                    <TableHead className="font-semibold">Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-400 py-10">
                        {searchTerm ? `No staff matching "${searchTerm}"` : 'No staff in this team'}
                      </TableCell>
                    </TableRow>
                  ) : sorted.map(s => {
                    const att = s.attendance;
                    const activity = getActivityStatus(att, currentTime);
                    const isActive = att?.check_in && !att?.check_out;
                    const breakSecs = isActive ? getBreakSecs(att, currentTime) : 0;
                    const workSecs = isActive ? getWorkSecs(att, currentTime) : 0;
                    const completedBreakMins = att?.breaks
                      ?.filter(b => b.end_time)
                      .reduce((s, b) => s + (b.duration || 0), 0) || 0;

                    return (
                      <TableRow
                        key={s.staff_id}
                        data-testid={`row-${s.staff_id}`}
                        className={`hover:bg-gray-50 transition-colors
                          ${activity.status === 'on_break' ? 'bg-orange-50' : ''}
                          ${activity.status === 'ready' ? 'bg-emerald-50' : ''}
                        `}
                      >
                        {/* Name */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-emerald-700 font-semibold text-sm">
                                {s.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">{s.name}</p>
                              {s.department && (
                                <p className="text-xs text-gray-400">{s.department}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        {/* Status badge */}
                        <TableCell>
                          {att ? (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold
                              ${att.status === 'present' ? 'bg-green-100 text-green-700' : ''}
                              ${att.status === 'late' ? 'bg-amber-100 text-amber-700' : ''}
                              ${att.status === 'absent' ? 'bg-red-100 text-red-700' : ''}
                              ${att.status === 'day_off' ? 'bg-blue-100 text-blue-700' : ''}
                              ${!['present','late','absent','day_off'].includes(att.status) ? 'bg-gray-100 text-gray-600' : ''}
                            `}>
                              {att.status === 'present' ? 'Present'
                                : att.status === 'late' ? 'Late'
                                : att.status === 'absent' ? 'Absent'
                                : att.status === 'day_off' ? 'Day Off'
                                : att.status || 'Not Marked'}
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                              Not Marked
                            </span>
                          )}
                        </TableCell>

                        {/* Check In */}
                        <TableCell>
                          {att?.check_in
                            ? <span className="text-green-600 font-medium text-sm">{formatTimeFromISO(att.check_in, att.display_timezone || member.timezone)}</span>
                            : <span className="text-gray-300">—</span>}
                        </TableCell>

                        {/* Check Out */}
                        <TableCell>
                          {att?.check_out
                            ? <span className="text-blue-600 font-medium text-sm">{formatTimeFromISO(att.check_out, att.display_timezone || member.timezone)}</span>
                            : <span className="text-gray-300">—</span>}
                        </TableCell>

                        {/* Work Time */}
                        <TableCell>
                          {isActive && workSecs > 0 ? (
                            <span className="font-mono text-sm text-green-700 font-medium">
                              {fmtDuration(workSecs)}
                            </span>
                          ) : att?.check_out && att?.check_in ? (
                            <span className="text-sm text-gray-600">
                              {fmtMins(Math.floor(
                                (new Date(att.check_out) - new Date(att.check_in)) / 60000
                                - completedBreakMins
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </TableCell>

                        {/* Break */}
                        <TableCell>
                          {isActive && breakSecs > 0 ? (
                            <span className="font-mono text-sm text-orange-600 font-medium">
                              {fmtDuration(breakSecs)}
                              {att.breaks?.length > 0 && (
                                <span className="ml-1 text-xs text-gray-400">({att.breaks.length})</span>
                              )}
                            </span>
                          ) : completedBreakMins > 0 ? (
                            <span className="text-sm text-orange-600">
                              {fmtMins(completedBreakMins)}
                              {att.breaks?.filter(b=>b.end_time).length > 0 && (
                                <span className="ml-1 text-xs text-gray-400">({att.breaks.filter(b=>b.end_time).length})</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </TableCell>

                        {/* Late By */}
                        <TableCell>
                          {att?.late_by
                            ? <span className="text-amber-600 font-semibold text-sm">{att.late_by} min</span>
                            : <span className="text-gray-300">—</span>}
                        </TableCell>

                        {/* Activity */}
                        <TableCell>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${activity.color}`}>
                            {activity.status === 'on_break' && <Coffee className="w-3.5 h-3.5 animate-pulse" />}
                            {activity.status === 'ready' && <CheckCircle className="w-3.5 h-3.5" />}
                            {activity.status === 'checked_out' && <LogOut className="w-3.5 h-3.5" />}
                            {activity.status === 'force_out' && <AlertCircle className="w-3.5 h-3.5" />}
                            {activity.status === 'not_in' && <UserX className="w-3.5 h-3.5" />}
                            <span>{activity.label}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default ManagerAttendancePage;
